import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { LogoSettings, LogoSource } from '@/integrations/supabase/rpc-types';

export const LOGO_SETTINGS_KEY = 'logo-settings';

type LogoVariant = 'logo' | 'logo_small';

export const useLogoSettings = () => {
  const { userData, userRole, refreshUserData } = useAuth();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: [LOGO_SETTINGS_KEY, userData?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_logo_settings' as any);
      if (error) throw error;
      return data as unknown as LogoSettings;
    },
    enabled: !!userData?.id,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Ricarica il tab e l'utente in AuthContext, che ricalcola il logo della sidebar
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: [LOGO_SETTINGS_KEY] });
    await refreshUserData();
  };

  const uploadLogo = async (file: File, variant: LogoVariant): Promise<string> => {
    if (!userData?.id) throw new Error('User ID not available');
    const ext = file.name.split('.').pop() || 'png';
    const folder = userRole === 'agency' ? 'agencies' : 'users';
    const path = `${folder}/${userData.id}/${variant}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('p2b')
      .upload(path, file, { upsert: true, contentType: file.type });
    if (uploadError) throw new Error(uploadError.message);

    const { data: urlData } = supabase.storage.from('p2b').getPublicUrl(path);
    const url = `${urlData.publicUrl}?t=${Date.now()}`;

    const { error } = variant === 'logo'
      ? await supabase.rpc('update_my_logo_url' as any, { p_logo_url: url })
      : await supabase.rpc('update_my_logo_small_url' as any, { p_logo_small_url: url });
    if (error) throw new Error(error.message);

    await refresh();
    return url;
  };

  const setLogoSource = async (source: LogoSource) => {
    const { error } = await supabase.rpc('update_my_logo_source' as any, { p_source: source });
    if (error) throw new Error(error.message);
    await refresh();
  };

  return { settings, isLoading, uploadLogo, setLogoSource };
};
